import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable, combineLatest, forkJoin, of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';

import { NotificationService } from '@app/core/services/notification.service';
import { AvaliacaoService } from '@core/config/services/avaliacao.service';
import { ContextApiService } from '@core/context/services/context-api.service';
import { ContextService } from '@core/context/services/context.service';
import { MobileContextService } from '@core/context/services/mobile-context.service';
import { ErrorHandlerService } from '@core/error-handling/services/error-handler.service';
import { CalendarioService } from '@features/frequencia/services/calendario.service';
import { AnexoItem } from '@infrastructure/ui/components/anexos/anexos.component';
import { PrototypeSelectOption } from '@infrastructure/ui/components/forms/prototype-select/prototype-select.component';
import { PeriodoSelectOption } from '@infrastructure/ui/components/forms/periodo-select/periodo-select.component';

import {
  INSTRUMENTO_ANEXO_EXTENSOES_PERMITIDAS,
  INSTRUMENTO_ANEXO_TAMANHO_MAXIMO,
  InstrumentoArquivoState,
  InstrumentoCampoAdicionalConfig,
  InstrumentoFormularioState,
  InstrumentoItemEducacional,
  InstrumentoResumoView,
  InstrumentoTipoOption,
  InstrumentoTurmaOption,
  TipoInstrumentoAvaliacaoId
} from '../models/instrumento-avaliacao.types';
import {
  InstrumentoArquivoAnexo,
  InstrumentoAvaliacaoRegistro,
  InstrumentoItemEducacionalOption,
  InstrumentoAvaliacaoService
} from '../services/instrumento-avaliacao.service';
import { InstrumentosAvaliacaoFacadeService } from '../services/instrumentos-avaliacao-facade.service';
import {
  aplicarPublicacaoInstrumento,
  buildContextKey,
  buildInstrumentoAnexosAccept,
  buildInstrumentoAnexosLabel,
  cloneInstrumentoCampoAdicional,
  construirInstrumentoPayload,
  createInstrumentoFormulario,
  encontrarRecuperacaoParalelaVinculada,
  extractTurmaIdFromRaw,
  formatarDataHoraPublicacao,
  getTipoInstrumentoLabel,
  hasInstrumentoCampoAdicionalConteudo,
  isPeriodoEncerradoComLista,
  isPeriodoLetivoEncerrado,
  isTurmaEjaModular,
  normalizarCampoAdicionalParaPayload,
  sanitizeInstrumentoCampoNumerico,
  validarInstrumentoFormulario
} from '../utils/instrumento-avaliacao.utils';
import { InstrumentoAvaliacaoModalComponent } from './instrumento-avaliacao-modal.component';
import { InstrumentoAvaliacaoHostBase } from '../models/instrumento-avaliacao-host.base';
import { InstrumentoFormularioHost } from '../models/instrumento-formulario-host.interface';

interface TurmaContexto {
  id: number;
  descricao?: string;
  nome?: string;
  tipoAtendimento?: string;
  modalidade?: string;
  separacaoAlunosEmGrupos?: boolean;
  tipoResultado?: string;
  estabelecimento?: { id: number; nome?: string };
  etapaMatriz?: { modular?: boolean };
}

export interface PeriodoContexto {
  id: number;
  descricao: string;
  dataInicial: string;
  dataFinal: string;
}

export type InstrumentoCampoNumerico = 'notaMinima' | 'notaMaxima' | 'peso';

@Component({
  selector: 'app-instrumentos-avaliacao',
  templateUrl: './instrumentos-avaliacao.component.html',
  styleUrls: ['./instrumentos-avaliacao.component.scss']
})
export class InstrumentosAvaliacaoComponent extends InstrumentoAvaliacaoHostBase implements OnInit, OnDestroy, InstrumentoFormularioHost {
  readonly vm = this;

  @ViewChild('instrumentoModalRef') instrumentoModalRef?: InstrumentoAvaliacaoModalComponent;

  protected destroy$ = new Subject<void>();
  private closeInstrumentoFormulario$ = new Subject<void>();

  turma: TurmaContexto | null = null;
  periodo: PeriodoContexto | null = null;
  periodos: PeriodoContexto[] = [];
  itemEducacional: InstrumentoItemEducacional | null = null;
  itensEducacionais: InstrumentoItemEducacional[] = [];
  itensEducacionaisTotal: InstrumentoItemEducacional[] = [];
  contextoItemEducacionalIds: number[] = [];
  carregandoInicial = false;
  turmaSemCalendario = false;

  constructor(
    private contextService: ContextService,
    private contextApiService: ContextApiService,
    protected mobileContextService: MobileContextService,
    private avaliacaoService: AvaliacaoService,
    protected instrumentoAvaliacaoService: InstrumentoAvaliacaoService,
    instrumentosFacade: InstrumentosAvaliacaoFacadeService,
    protected notificationService: NotificationService,
    private errorHandler: ErrorHandlerService,
    protected calendarioService: CalendarioService
  ) {
    super(instrumentosFacade);
  }

  ngOnInit(): void {
    this.initializeComponent().catch(() => undefined);

    let contextoKey = buildContextKey(this.mobileContextService.getContextoAtual());
    this.mobileContextService.contexto$
      .pipe(takeUntil(this.destroy$))
      .subscribe((contexto) => {
        const nextKey = buildContextKey(contexto);
        if (nextKey !== contextoKey) {
          contextoKey = nextKey;
          this.initializeComponent().catch(() => undefined);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.instrumentosFacade.reset();
  }

  get labelItensEducacionais(): string {
    return this.instrumentoTurmaEAtividade ? 'atividade' : 'componente curricular';
  }

  get instrumentosResumo(): InstrumentoResumoView[] {
    return this.instrumentosTelaResumo;
  }

  get instrumentoContextoResumo(): InstrumentoResumoView[] {
    return (this.instrumentoContextoRegistros || []).map((registro) => this.toInstrumentoResumo(registro));
  }

  get instrumentosTelaResumo(): InstrumentoResumoView[] {
    return (this.instrumentosTelaRegistros || []).map((registro) => this.toInstrumentoResumo(registro));
  }

  get instrumentoTurmaSelecionada(): InstrumentoTurmaOption | null {
    return this.instrumentoTurmasDisponiveis.find((turma) => turma.id === this.instrumentoForm.turmaId) || null;
  }

  get instrumentoItemSelecionado(): InstrumentoItemEducacional | null {
    return this.instrumentoItensDisponiveis.find((item) => item.id === this.instrumentoForm.itemEducacionalId) || null;
  }

  get instrumentoPeriodoSelecionado(): PeriodoContexto | null {
    const periodoDetalhado = this.findInstrumentoPeriodoDetalhado(this.instrumentoForm.periodoAvaliativoId);
    const periodoAvaliativo = periodoDetalhado?.periodoAvaliativo;
    if (!periodoDetalhado && !periodoAvaliativo) {
      return null;
    }

    return {
      id: Number(periodoAvaliativo?.id ?? periodoDetalhado?.id ?? 0),
      descricao: String(periodoAvaliativo?.descricao || periodoDetalhado?.descricao || ''),
      dataInicial: String(periodoAvaliativo?.dataInicial || periodoDetalhado?.dataInicial || ''),
      dataFinal: String(periodoAvaliativo?.dataFinal || periodoDetalhado?.dataFinal || '')
    };
  }

  get instrumentoTurmaEAtividade(): boolean {
    return this.instrumentoTurmaSelecionada?.tipoAtendimento === 'ATENDIMENTO_EDUCACIONAL_ESPECIALIZADO' ||
      this.instrumentoTurmaSelecionada?.tipoAtendimento === 'ATIVIDADE_COMPLEMENTAR';
  }

  get instrumentoTurmaEAtividadeComplementar(): boolean {
    return this.instrumentoTurmaSelecionada?.tipoAtendimento === 'ATIVIDADE_COMPLEMENTAR';
  }

  get turmaEjaModular(): boolean {
    return isTurmaEjaModular(this.turma);
  }

  get instrumentoTurmaEjaModular(): boolean {
    return isTurmaEjaModular(this.instrumentoTurmaSelecionada);
  }

  get instrumentoTurmaOptions(): PrototypeSelectOption[] {
    return this.instrumentoTurmasDisponiveis.map((turma) => ({ value: turma.id, label: turma.descricaoExibicao }));
  }

  get instrumentoItemEducacionalOptions(): PrototypeSelectOption[] {
    return this.instrumentoItensDisponiveis.map((item) => ({ value: item.id, label: item.descricao }));
  }

  get instrumentoPeriodoEncerradoSelecionado(): boolean {
    const periodo = this.instrumentoPeriodoSelecionado;
    if (!periodo) {
      return false;
    }

    return this.isPeriodoEncerradoComLista(periodo, this.instrumentoEncerramentosTurma);
  }

  get tiposInstrumentoDisponiveis(): InstrumentoTipoOption[] {
    const options: InstrumentoTipoOption[] = [{ id: 'INSTRUMENTO_AVALIACAO', label: 'Instrumento de avaliação' }];

    if (this.instrumentoConfiguracaoEstabelecimento?.permiteRecuperacaoParalela === true) {
      options.push({ id: 'RECUPERACAO_PARALELA', label: 'Recuperação paralela' });
    }

    if (!this.instrumentoTurmaEAtividadeComplementar && !this.instrumentoTurmaEjaModular && this.instrumentoConfiguracaoEstabelecimento?.permiteRecuperacaoPeriodoAvaliativo === true) {
      options.push({ id: 'RECUPERACAO_PERIODO_AVALIATIVO', label: 'Recuperação do período avaliativo' });
    }

    if (this.instrumentoConfiguracaoEstabelecimento?.permiteNotaComplementar === true) {
      options.push({ id: 'PONTO_EXTRA', label: 'Nota Complementar/Ponto Extra' });
    }

    return options;
  }

  get instrumentoTipoOptions(): PrototypeSelectOption[] {
    return this.tiposInstrumentoDisponiveis.map((tipo) => ({ value: tipo.id, label: tipo.label }));
  }

  get instrumentoPeriodoOptions(): PeriodoSelectOption[] {
    return this.instrumentoPeriodos.map((periodo) => {
      const periodoAvaliativo = periodo.periodoAvaliativo;
      const id = Number(periodoAvaliativo?.id ?? periodo.id ?? 0);
      const descricao = String(periodoAvaliativo?.descricao || periodo.descricao || 'Período avaliativo');
      const dataInicial = String(periodoAvaliativo?.dataInicial || periodo.dataInicial || '');
      const dataFinal = String(periodoAvaliativo?.dataFinal || periodo.dataFinal || '');

      return {
        id,
        descricao,
        dataInicial,
        dataFinal,
        encerrado: this.isPeriodoEncerradoComLista({ id, descricao, dataInicial, dataFinal }, this.instrumentoEncerramentosTurma)
      };
    });
  }

  get instrumentosRecuperacaoOptions(): InstrumentoResumoView[] {
    return this.instrumentoContextoResumo.filter((item) => item.tipoInstrumento === 'INSTRUMENTO_AVALIACAO' && item.id !== this.instrumentoForm.id);
  }

  get instrumentoRecuperacaoSelectOptions(): PrototypeSelectOption[] {
    return this.instrumentosRecuperacaoOptions.map((instrumento) => ({
      value: instrumento.id,
      label: `${instrumento.sigla} - ${instrumento.descricao}`
    }));
  }

  get shouldShowInstrumentoPeriodoField(): boolean {
    return !this.instrumentoTurmaEAtividade;
  }

  get instrumentoTurmaBloqueadaContexto(): boolean {
    return false;
  }

  get instrumentoExibeFaixaNotas(): boolean {
    return this.getModoAvaliacaoInstrumentoAtual() === 'NUMERICO';
  }

  get instrumentoExibePeso(): boolean {
    return this.getModoAvaliacaoInstrumentoAtual() === 'NUMERICO' || this.getModoAvaliacaoInstrumentoAtual() === 'CONCEITO_NUMERICO';
  }

  get instrumentoFormularioTitulo(): string {
    return this.instrumentoForm.id ? 'Editando instrumento de avaliação' : 'Adicionando instrumento de avaliação';
  }

  get instrumentoDataSelecionada(): Date | null {
    if (!this.instrumentoForm.data) return null;
    const [ano, mes, dia] = this.instrumentoForm.data.split('-').map(Number);
    return new Date(ano, mes - 1, dia, 12, 0, 0);
  }

  onInstrumentoDataChange(data: Date | null): void {
    if (!data) {
      this.instrumentoForm.data = '';
      return;
    }
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    this.instrumentoForm.data = `${ano}-${mes}-${dia}`;
  }

  get instrumentoErrosFormulario(): string[] {
    return this.validarFormularioInstrumento();
  }

  get instrumentoFormularioValido(): boolean {
    return this.instrumentoCamposObrigatoriosPreenchidos();
  }

  private instrumentoCamposObrigatoriosPreenchidos(): boolean {
    if (!this.instrumentoForm.turmaId) return false;
    if (!this.instrumentoForm.itemEducacionalId) return false;
    if (this.shouldShowInstrumentoPeriodoField && !this.instrumentoForm.periodoAvaliativoId) return false;
    if (!this.instrumentoForm.data) return false;
    if (!this.instrumentoForm.tipoInstrumento) return false;
    if (!this.instrumentoForm.descricao.trim()) return false;
    const sigla = this.instrumentoForm.sigla.trim();
    if (!sigla || sigla.length > 5) return false;
    if (this.instrumentoForm.tipoInstrumento === 'RECUPERACAO_PARALELA' && !this.instrumentoForm.instrumentoRecuperacaoId) return false;
    if (this.instrumentoTurmaEjaModular) return false;
    if (!this.instrumentoCamposAdicionaisObrigatoriosValidos) return false;
    return true;
  }

  onInstrumentoCamposAdicionaisValidChange(valid: boolean): void {
    this.instrumentoCamposAdicionaisObrigatoriosValidos = valid;
  }

  get instrumentoRecuperacaoVinculadaErros(): string[] {
    return this.validarRecuperacaoVinculada();
  }

  get instrumentoEdicaoSomenteDataPermitida(): boolean {
    return !!(this.instrumentoForm.id && this.instrumentoPeriodoEncerradoSelecionado);
  }

  get instrumentoFoiAlterado(): boolean {
    return this.serializeInstrumentoForm() !== this.instrumentoSnapshotInicial ||
      this.serializeInstrumentoArquivos() !== this.instrumentoArquivosSnapshotInicial ||
      this.serializeInstrumentoCampoAdicional() !== this.instrumentoCampoAdicionalSnapshotInicial;
  }

  get instrumentoLabelItemEducacional(): string {
    return this.instrumentoTurmaEAtividade ? 'Atividade' : 'Componente curricular';
  }

  get instrumentoAnexosItems(): AnexoItem[] {
    return this.instrumentoArquivos.map((arquivo, index) => ({
      id: this.resolveInstrumentoArquivoIdentificador(arquivo, index),
      nome: arquivo.nome,
      tamanho: arquivo.tamanho,
      uploading: arquivo.uploading
    }));
  }

  get instrumentoAnexosLabel(): string {
    return buildInstrumentoAnexosLabel(INSTRUMENTO_ANEXO_EXTENSOES_PERMITIDAS);
  }

  get instrumentoAnexosAccept(): string {
    return buildInstrumentoAnexosAccept(INSTRUMENTO_ANEXO_EXTENSOES_PERMITIDAS);
  }

  get instrumentoAnexosTamanhoMaximoMb(): number {
    return Math.round(INSTRUMENTO_ANEXO_TAMANHO_MAXIMO / (1024 * 1024));
  }

  get instrumentoDescricaoPeriodo(): string {
    return this.instrumentoPeriodoSelecionado?.descricao || '';
  }

  get instrumentoNotaMinimaLimite(): number {
    const notaMinimaConfig = Number((this.instrumentoConfiguracaoTurma as { notaMinima?: number } | null)?.notaMinima);
    return !Number.isNaN(notaMinimaConfig) ? notaMinimaConfig : 0;
  }

  get instrumentoNotaMaximaLimite(): number {
    const notaMaximaConfig = Number((this.instrumentoConfiguracaoTurma as { notaMaxima?: number } | null)?.notaMaxima);
    return !Number.isNaN(notaMaximaConfig) ? notaMaximaConfig : 100;
  }

  get instrumentoPesoMinimoLimite(): number {
    return 1;
  }

  get instrumentoPesoMaximoLimite(): number {
    return 100;
  }

  get instrumentoNotaMinimaFormatada(): string {
    const valor = this.instrumentoForm.notaMinima;
    return valor ? String(valor).replace('.', ',') : '';
  }

  get instrumentoNotaMaximaFormatada(): string {
    const valor = this.instrumentoForm.notaMaxima;
    return valor ? String(valor).replace('.', ',') : '';
  }

  get instrumentoPesoFormatado(): string {
    const valor = this.instrumentoForm.peso;
    return valor ? String(valor).replace('.', ',') : '';
  }

  async openCadInstrumentoAvaliacao(): Promise<void> {
    this.instrumentoRegistroOriginal = null;
    this.instrumentosFacade.abrirCadastro({
      turmaId: this.turma?.id || null,
      itemEducacionalId: null,
      periodoAvaliativoId: null
    });
    this.resetInstrumentoCampoAdicional();
    this.syncInstrumentoFormWithContext();

    await Promise.all([
      this.loadDescricoesRecentesInstrumento(),
      this.carregarDadosFormularioInstrumento()
    ]);
    await this.carregarCampoAdicionalInstrumento(true);

    this.instrumentoSnapshotInicial = this.serializeInstrumentoForm();
    this.instrumentoArquivosSnapshotInicial = this.serializeInstrumentoArquivos();
    this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
    this.instrumentoCarregando = false;
  }

  fecharFormularioInstrumento(): void {
    this.instrumentosFacade.fecharFormulario();
    this.resetInstrumentoCampoAdicional();
    this.closeInstrumentoFormulario$.next();
    this.closeInstrumentoFormulario$.complete();
    this.closeInstrumentoFormulario$ = new Subject<void>();
  }

  selecionarDescricaoRecenteInstrumento(descricao: string): void {
    this.instrumentosFacade.selecionarDescricaoRecente(descricao);
  }

  editarInstrumento(instrumento: InstrumentoResumoView): void {
    if (!instrumento?.id || !this.podeEditarInstrumento(instrumento)) {
      return;
    }

    this.instrumentosFacade.abrirEdicao();
    this.resetInstrumentoCampoAdicional();

    Promise.all([
      this.loadDescricoesRecentesInstrumento(),
      new Promise<void>((resolve, reject) => {
        this.instrumentoAvaliacaoService.get(instrumento.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: async (registro) => {
              const registroOriginal = { ...registro } as InstrumentoAvaliacaoRegistro;
              if (registroOriginal.version === undefined || registroOriginal.version === null) {
                registroOriginal.version = (instrumento.raw as { version?: number | null }).version ?? null;
              }
              if (registroOriginal.versao === undefined || registroOriginal.versao === null) {
                registroOriginal.versao = (instrumento.raw as { versao?: number | null }).versao ?? null;
              }

              this.instrumentoRegistroOriginal = registroOriginal;
              this.instrumentoForm = this.mapInstrumentoToForm(registroOriginal);
              this.instrumentoArquivos = this.mapInstrumentoArquivos((registroOriginal as any).arquivos as InstrumentoArquivoAnexo[] | null | undefined);
              // Campo adicional de instrumento é transient e deve ser carregado via endpoint dedicado.
              this.instrumentoCampoAdicional = null;
              this.instrumentoCamposAdicionaisCarregadoParaId = null;
              await this.carregarDadosFormularioInstrumento(registroOriginal);
              await this.carregarCampoAdicionalInstrumento(true);
              this.instrumentoSnapshotInicial = this.serializeInstrumentoForm();
              this.instrumentoArquivosSnapshotInicial = this.serializeInstrumentoArquivos();
              this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
              this.instrumentoCarregando = false;
              resolve();
            },
            error: (error) => {
              this.instrumentoCarregando = false;
              this.instrumentoFormularioAberto = false;
              this.errorHandler.handleError(error);
              reject(error);
            }
          });
      })
    ]).catch(() => {
      this.instrumentoCarregando = false;
    });
  }

  salvarInstrumento(adicionarNovo = false): void {
    if (this.instrumentoAnexosEnviando) {
      this.notificationService.warning({
        title: 'Aguarde o envio',
        message: 'Finalize o upload dos anexos antes de salvar o instrumento.'
      });
      return;
    }

    const erros = this.validarFormularioInstrumento();
    if (erros.length) {
      this.notificationService.warning({ title: 'Revise o formulário', message: erros[0] });
      return;
    }

    const payload = this.buildInstrumentoPayload();
    this.instrumentoSalvando = true;

    const recuperacaoVinculada = this.getRecuperacaoParalelaVinculada(this.instrumentoForm.id || null);
    if (this.deveAtualizarRecuperacaoVinculada(payload, recuperacaoVinculada)) {
      this.salvarInstrumentoComRecuperacaoVinculada(payload, recuperacaoVinculada!, adicionarNovo);
      return;
    }

    this.instrumentoAvaliacaoService.save(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.finalizarSucessoInstrumento(adicionarNovo),
        error: (error) => {
          this.instrumentoSalvando = false;
          this.errorHandler.handleError(error);
        }
      });
  }

  confirmarExclusaoInstrumento(instrumento: InstrumentoResumoView): void {
    if (!this.podeExcluirInstrumento(instrumento)) {
      return;
    }

    this.instrumentosFacade.abrirConfirmacaoExclusao(instrumento);
  }

  confirmarExclusaoInstrumentoEmEdicao(): void {
    const instrumento = this.instrumentosTelaResumo.find((item) => item.id === this.instrumentoForm.id);
    if (!instrumento) {
      return;
    }
    this.fecharFormularioInstrumento();
    this.confirmarExclusaoInstrumento(instrumento);
  }

  togglePublicacaoInstrumento(instrumento: InstrumentoResumoView): void {
    if (!this.podeAlternarPublicacaoInstrumento(instrumento)) {
      return;
    }

    const publicado = instrumento.publico;
    this.instrumentosFacade.abrirConfirmacaoPublicacao(
      instrumento,
      !publicado,
      publicado ? 'Desfazer publicação' : 'Publicar instrumento'
    );

  }

  fecharConfirmacaoInstrumento(): void {
    this.instrumentosFacade.fecharConfirmacao();
  }

  confirmarAcaoInstrumento(): void {
    const instrumento = this.instrumentoConfirmacao.instrumento;
    if (!instrumento?.id) {
      this.fecharConfirmacaoInstrumento();
      return;
    }

    const instrumentoId = instrumento.id;

    if (this.instrumentoConfirmacao.tipo === 'excluir') {
      if (!this.instrumentoConfirmacao.confirmadoExclusao) {
        this.notificationService.warning({
          title: 'Confirmação necessária',
          message: 'Marque a confirmação para continuar com a exclusão do instrumento.'
        });
        return;
      }

      this.instrumentoSalvando = true;
      this.instrumentoAvaliacaoService.remove(instrumento.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.instrumentoSalvando = false;
            this.fecharConfirmacaoInstrumento();
            this.notificationService.info({ title: 'Instrumento excluído', message: 'O instrumento de avaliação foi removido com sucesso.' });
            this.loadInstrumentosTelaAtual().catch(() => undefined);
            this.fecharFormularioInstrumento();
          },
          error: (error) => {
            this.instrumentoSalvando = false;
            this.fecharConfirmacaoInstrumento();
            this.errorHandler.handleError(error);
          }
        });
      return;
    }

    const turmaId = instrumento.turmaId || this.turma?.id || Number((((instrumento.raw as any).turma as { id?: number } | undefined)?.id) || 0) || null;
    if (!turmaId) {
      this.instrumentoPublicandoId = null;
      this.fecharConfirmacaoInstrumento();
      this.notificationService.warning({ title: 'Publicação indisponível', message: 'Não foi possível identificar a turma do instrumento para concluir a publicação.' });
      return;
    }

    this.instrumentoPublicandoId = instrumento.id;
    this.instrumentoAvaliacaoService.publicar(turmaId, instrumento.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.instrumentoPublicandoId = null;
          this.fecharConfirmacaoInstrumento();
          const publicado = !!(response?.publicavel?.publicado ?? response?.publicado ?? this.instrumentoConfirmacao.publicar);
          this.atualizarEstadoPublicacaoInstrumento(instrumentoId, publicado, response?.dataPublicacao || response?.publicavel?.dataPublicacao || null);
          this.notificationService.success({
            title: publicado ? 'Instrumento publicado' : 'Publicação desfeita',
            message: this.getMensagemSucessoPublicacaoInstrumento(instrumento, publicado)
          });
        },
        error: (error) => {
          this.instrumentoPublicandoId = null;
          this.fecharConfirmacaoInstrumento();
          this.errorHandler.handleError(error);
        }
      });
  }

  onInstrumentoTurmaSelectChange(value: number | string | null): void {
    this.instrumentoForm.turmaId = this.toNullableNumber(value);
    this.onInstrumentoTurmaChange();
  }

  onInstrumentoItemEducacionalSelectChange(value: number | string | null): void {
    this.instrumentoForm.itemEducacionalId = this.toNullableNumber(value);
    this.onInstrumentoItemEducacionalChange();
  }

  onInstrumentoPeriodoSelectChange(value: number | string | null): void {
    this.instrumentoForm.periodoAvaliativoId = this.toNullableNumber(value);
    this.onInstrumentoPeriodoChange();
  }

  onInstrumentoTipoSelectChange(value: number | string | null): void {
    this.instrumentoForm.tipoInstrumento = typeof value === 'string' ? value as TipoInstrumentoAvaliacaoId : null;
    this.onInstrumentoTipoChange();
  }

  onInstrumentoRecuperacaoSelectChange(value: number | string | null): void {
    this.instrumentoForm.instrumentoRecuperacaoId = this.toNullableNumber(value);
    this.onInstrumentoRecuperacaoChange();
  }

  onInstrumentoAbaChange(tabId: string): void {
    if (tabId === 'informacoes' || tabId === 'anexos' || tabId === 'dadosAdicionais') {
      this.instrumentoAbaAtiva = tabId;
    }
  }

  onInstrumentoTurmaChange(): void {
    this.instrumentoForm.itemEducacionalId = null;
    this.instrumentoForm.periodoAvaliativoId = null;
    this.instrumentoForm.instrumentoRecuperacaoId = null;
    this.instrumentoItensDisponiveis = [];
    this.instrumentoPeriodos = [];
    this.instrumentoContextoRegistros = [];
    this.instrumentoCalendario = null;
    this.instrumentoConfiguracaoEstabelecimento = null;
    this.instrumentoConfiguracaoTurma = null;
    this.instrumentoEncerramentosTurma = [];
    this.instrumentoRecuperacaoDetalhes.clear();

    if (this.instrumentoForm.turmaId) {
      this.carregarDependenciasTurmaInstrumento().catch(() => undefined);
    }
  }

  onInstrumentoItemEducacionalChange(): void {
    this.instrumentoForm.periodoAvaliativoId = null;
    this.instrumentoForm.instrumentoRecuperacaoId = null;
    this.instrumentoPeriodos = [];
    this.instrumentoContextoRegistros = [];
    this.instrumentoRecuperacaoDetalhes.clear();

    if (this.instrumentoForm.itemEducacionalId) {
      Promise.all([this.loadInstrumentoPeriodos(), this.loadInstrumentosContexto()]).catch(() => undefined);
    }
  }

  onInstrumentoPeriodoChange(): void {
    this.instrumentoForm.instrumentoRecuperacaoId = null;
    this.instrumentoRecuperacaoDetalhes.clear();
    this.loadInstrumentosContexto().catch(() => undefined);
  }

  onInstrumentoTipoChange(): void {
    this.instrumentoForm.instrumentoRecuperacaoId = null;
    this.instrumentoRecuperacaoDetalhes.clear();
    if (this.instrumentoForm.tipoInstrumento === 'RECUPERACAO_PARALELA') {
      this.instrumentoForm.notaMinima = '';
      this.instrumentoForm.notaMaxima = '';
    }
  }

  onInstrumentoRecuperacaoChange(): void {
    const instrumentoRecuperacao = this.instrumentosRecuperacaoOptions.find((item) => item.id === this.instrumentoForm.instrumentoRecuperacaoId);
    if (!instrumentoRecuperacao) {
      this.instrumentoForm.modoAvaliacao = this.getModoAvaliacaoInstrumentoAtual();
      return;
    }

    const raw = instrumentoRecuperacao.raw as any;
    this.instrumentoForm.conteudo = String(raw.conteudo || this.instrumentoForm.conteudo || '');
    this.instrumentoForm.criteriosAvaliativos = String(raw.criteriosAvaliativos || this.instrumentoForm.criteriosAvaliativos || '');
    this.instrumentoForm.notaMinima = raw.notaMinima === null || raw.notaMinima === undefined ? '' : String(raw.notaMinima);
    this.instrumentoForm.notaMaxima = raw.notaMaxima === null || raw.notaMaxima === undefined ? '' : String(raw.notaMaxima);
    this.instrumentoForm.modoAvaliacao = instrumentoRecuperacao.modoAvaliacao;
    this.preloadInstrumentoRecuperacaoDetalhes(instrumentoRecuperacao.id);
  }

  onInstrumentoAnexosSelecionados(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files || []);
    if (!files.length) {
      return;
    }

    this.processarInstrumentoAnexos(files);
    if (input) {
      input.value = '';
    }
  }

  onInstrumentoArquivoSelecionado(file: File): void {
    if (!file) {
      return;
    }

    this.processarInstrumentoAnexos([file]);
  }

  onInstrumentoArquivosSelecionados(files: File[]): void {
    if (!files?.length) {
      return;
    }

    this.processarInstrumentoAnexos(files);
  }

  baixarInstrumentoAnexoCompartilhado(anexo: AnexoItem): void {
    const arquivo = this.instrumentoArquivos.find((item, index) => this.resolveInstrumentoArquivoIdentificador(item, index) === anexo.id);
    if (!arquivo) {
      this.notificationService.warning({ title: 'Download indisponível', message: 'O arquivo informado não foi encontrado.' });
      return;
    }

    this.baixarInstrumentoAnexo(arquivo);
  }

  removerInstrumentoAnexoCompartilhado(anexoId: number | string): void {
    this.instrumentoArquivos = this.instrumentoArquivos.filter((arquivo, index) => this.resolveInstrumentoArquivoIdentificador(arquivo, index) !== anexoId);
  }

  fecharRecuperacaoVinculada(): void {
    this.instrumentosFacade.fecharRecuperacaoVinculada();
  }

  confirmarRecuperacaoVinculada(): void {
    const erros = this.instrumentoRecuperacaoVinculadaErros;
    if (erros.length) {
      this.notificationService.warning({ title: 'Revise os dados da recuperação paralela', message: erros[0] });
      return;
    }

    const instrumentoBase = this.instrumentoRecuperacaoVinculada.instrumentoBase;
    const instrumentoRecuperacao = this.instrumentoRecuperacaoVinculada.instrumentoRecuperacao;
    if (!instrumentoBase || !(instrumentoRecuperacao as any)?.id) {
      return;
    }

    this.instrumentoRecuperacaoVinculada.salvando = true;
    this.instrumentoSalvando = true;

    this.instrumentoAvaliacaoService.save(instrumentoBase as any)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (instrumentoSalvo) => {
          const payloadRecuperacao = this.buildRecuperacaoVinculadaPayload(instrumentoRecuperacao as any, instrumentoSalvo);
          this.instrumentoAvaliacaoService.updatePeriodoOrDate(payloadRecuperacao)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                const adicionarNovo = this.instrumentoRecuperacaoVinculada.adicionarNovo;
                this.fecharRecuperacaoVinculada();
                this.notificationService.info({
                  title: 'Recuperação paralela ajustada',
                  message: 'A recuperação paralela vinculada foi atualizada conforme a nova data/período do instrumento.'
                });
                this.finalizarSucessoInstrumento(adicionarNovo);
              },
              error: (error) => {
                this.instrumentoRecuperacaoVinculada.salvando = false;
                this.instrumentoSalvando = false;
                this.errorHandler.handleError(error);
              }
            });
        },
        error: (error) => {
          this.instrumentoRecuperacaoVinculada.salvando = false;
          this.instrumentoSalvando = false;
          this.errorHandler.handleError(error);
        }
      });
  }

  podeExcluirInstrumento(instrumento: InstrumentoResumoView): boolean {
    if (this.isExclusaoInstrumentoBloqueada(instrumento)) {
      if (this.isInstrumentoBloqueadoPorEncerramento(instrumento)) {
        this.notificationService.warning({ title: 'Período encerrado', message: 'Não é permitido excluir instrumentos de períodos avaliativos encerrados.' });
      } else {
        this.notificationService.warning({ title: 'Exclusão bloqueada', message: 'Não é permitido excluir instrumentos que possuem recuperação paralela vinculada.' });
      }
      return false;
    }

    return true;
  }

  podeAlternarPublicacaoInstrumento(instrumento: InstrumentoResumoView): boolean {
    if (this.isPublicacaoInstrumentoBloqueada(instrumento)) {
      this.notificationService.warning({ title: 'Publicação bloqueada', message: 'Não é permitido publicar ou desfazer a publicação quando o período avaliativo ou letivo estiver encerrado.' });
      return false;
    }

    return true;
  }

  isExclusaoInstrumentoBloqueada(instrumento: InstrumentoResumoView): boolean {
    return this.isInstrumentoBloqueadoPorEncerramento(instrumento) || !!this.getRecuperacaoParalelaVinculada(instrumento.id);
  }

  isPublicacaoInstrumentoBloqueada(instrumento: InstrumentoResumoView): boolean {
    return this.isInstrumentoBloqueadoPorEncerramento(instrumento) ||
      (!instrumento.periodoAvaliativoId && this.isPeriodoLetivoEncerrado(this.instrumentoEncerramentosTurma));
  }

  getItemEducacionalDescricao(instrumento: InstrumentoResumoView): string {
    const item = (instrumento?.raw as any)?.itemEducacional;
    return String(item?.descricao || item?.nome || this.itemEducacional?.descricao || 'Sem componente curricular');
  }

  trackByInstrumentoResumo(index: number, instrumento: InstrumentoResumoView): number {
    return instrumento.id || index;
  }

  private async initializeComponent(): Promise<void> {
    this.carregandoInicial = true;
    try {
      this.loadContextData();
      await this.enriquecerTurmaContextoSeNecessario();
      await Promise.all([
        this.loadItensEducacionaisFromAPI(),
        this.verificarCalendarioTurma()
      ]);
      await this.loadInstrumentosTelaAtual();
    } finally {
      this.carregandoInicial = false;
    }
  }

  private verificarCalendarioTurma(): Promise<void> {
    const turmaId = this.turma?.id;
    const isAtividadeOuModular = this.turma?.tipoAtendimento === 'ATIVIDADE_COMPLEMENTAR'
      || this.turma?.tipoAtendimento === 'ATENDIMENTO_EDUCACIONAL_ESPECIALIZADO'
      || !!this.turma?.etapaMatriz?.modular
      || this.turma?.modalidade === 'EJA_MODULAR';

    if (!turmaId || isAtividadeOuModular) {
      this.turmaSemCalendario = false;
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.instrumentoAvaliacaoService.getPeriodosAvaliativosTurma(turmaId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (periodos) => {
            this.turmaSemCalendario = !periodos || periodos.length === 0;
            resolve();
          },
          error: () => {
            this.turmaSemCalendario = false;
            resolve();
          }
        });
    });
  }

  private loadContextData(): void {
    // getTurma/getComponentes já priorizam MobileContextService; getPeriodo() é a única fonte de período (Fase 4.4 pendente).
    const turmaContexto = this.contextService.getTurma();
    const periodoContexto = this.contextService.getPeriodo();
    const componentesContexto = this.contextService.getComponentes();

    this.turma = turmaContexto ? {
      ...turmaContexto,
      descricao: turmaContexto.descricao || turmaContexto.nome || `Turma ${turmaContexto.id}`
    } : null;

    this.periodo = periodoContexto ? {
      id: Number(periodoContexto.id || 0),
      descricao: String(periodoContexto.descricao || ''),
      dataInicial: String(periodoContexto.dataInicial || ''),
      dataFinal: String(periodoContexto.dataFinal || '')
    } : null;

    this.periodos = this.periodo ? [this.periodo] : [];
    this.itensEducacionais = (componentesContexto || []).map((item: any) => ({
      id: item.id,
      descricao: String(item.descricao || item.nome || ''),
      isCampoExperiencia: false
    }));
    this.contextoItemEducacionalIds = this.itensEducacionais
      .map((item) => Number(item.id || 0))
      .filter((id) => id > 0);
    this.itensEducacionaisTotal = [...this.itensEducacionais];
    this.itemEducacional = this.itemEducacional && this.itensEducacionais.some((item) => item.id === this.itemEducacional?.id)
      ? this.itemEducacional
      : this.itensEducacionais[0] || null;
  }

  private enriquecerTurmaContextoSeNecessario(): Promise<void> {
    const turmaId = this.turma?.id;
    const precisaEnriquecer = !!this.turma
      && this.turma.etapaMatriz?.modular !== true
      && !this.turma.modalidade;

    if (!turmaId || !precisaEnriquecer) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.contextApiService.getTurma(turmaId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (turmaApi: any) => {
            const turmaAtual = this.turma;
            if (!turmaAtual) {
              resolve();
              return;
            }
            this.turma = {
              ...turmaAtual,
              id: turmaAtual.id,
              modalidade: turmaApi?.modalidade || turmaAtual.modalidade,
              etapaMatriz: turmaApi?.etapaMatriz ?? turmaAtual.etapaMatriz
            };
            resolve();
          },
          error: () => resolve()
        });
    });
  }

  private async loadItensEducacionaisFromAPI(): Promise<void> {
    if (!this.turma?.id) {
      return;
    }

    await new Promise<void>((resolve) => {
      this.instrumentoAvaliacaoService.getItensTurma(this.turma!.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (itens: InstrumentoItemEducacionalOption[]) => {
            const mapped = (itens || []).map((item) => ({
              id: item.id,
              descricao: item.descricao || item.nome || '',
              isCampoExperiencia: !!item.isCampoExperiencia,
              etapaMatrizDisciplina: item.etapaMatrizDisciplina as { id: number } | undefined
            }));
            if (mapped.length) {
              this.itensEducacionais = mapped;
              this.itensEducacionaisTotal = mapped;
              this.itemEducacional = mapped.find((item) => item.id === this.itemEducacional?.id) || mapped[0] || null;
            }
            resolve();
          },
          error: () => resolve()
        });
    });
  }

  private syncInstrumentoFormWithContext(): void {
    this.instrumentoForm.turmaId = this.instrumentoForm.turmaId || this.turma?.id || null;
  }

  private loadDescricoesRecentesInstrumento(): Promise<void> {
    return new Promise((resolve) => {
      this.instrumentoAvaliacaoService.getDescricoesRecentes(5)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (descricoes) => {
            this.instrumentoDescricoesRecentes = descricoes || [];
            resolve();
          },
          error: () => {
            this.instrumentoDescricoesRecentes = [];
            resolve();
          }
        });
    });
  }

  private async carregarDadosFormularioInstrumento(registro?: InstrumentoAvaliacaoRegistro): Promise<void> {
    await this.loadInstrumentoTurmas();

    if (registro?.turma?.id) {
      this.instrumentoForm.turmaId = registro.turma.id;
    }

    if (!this.instrumentoForm.turmaId && this.instrumentoTurmasDisponiveis.length) {
      this.instrumentoForm.turmaId = this.instrumentoTurmasDisponiveis[0].id;
    }

    await this.carregarDependenciasTurmaInstrumento();
  }

  private async carregarDependenciasTurmaInstrumento(): Promise<void> {
    await Promise.all([
      this.loadInstrumentoItensTurma(),
      this.loadInstrumentoConfiguracoes(),
      this.loadInstrumentoCalendario(),
      this.loadInstrumentoEncerramentos()
    ]);

    await Promise.all([
      this.loadInstrumentoPeriodos(),
      this.loadInstrumentosContexto()
    ]);
  }

  private loadInstrumentoTurmas(): Promise<void> {
    return new Promise((resolve) => {
      this.instrumentoAvaliacaoService.getTurmasContexto()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            const turmasDisponiveis = (response?.content || []).map((turma) => ({
              ...turma,
              descricaoExibicao: turma.descricao || turma.nome || `Turma ${turma.id}`
            }));

            if (this.instrumentoTurmaBloqueadaContexto && this.turma?.id) {
              const turmaContexto = turmasDisponiveis.find((turma) => turma.id === this.turma?.id);
              this.instrumentoTurmasDisponiveis = turmaContexto
                ? [turmaContexto]
                : [{ ...this.turma, descricaoExibicao: this.turma.descricao || `Turma ${this.turma.id}` } as InstrumentoTurmaOption];
            } else {
              this.instrumentoTurmasDisponiveis = turmasDisponiveis;
            }
            resolve();
          },
          error: () => {
            this.instrumentoTurmasDisponiveis = this.turma
              ? [{ ...this.turma, descricaoExibicao: this.turma.descricao || `Turma ${this.turma.id}` } as InstrumentoTurmaOption]
              : [];
            resolve();
          }
        });
    });
  }

  private loadInstrumentoItensTurma(): Promise<void> {
    if (!this.instrumentoForm.turmaId) {
      this.instrumentoItensDisponiveis = [];
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.instrumentoAvaliacaoService.getItensTurma(this.instrumentoForm.turmaId!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (itens: InstrumentoItemEducacionalOption[]) => {
            this.instrumentoItensDisponiveis = (itens || []).map((item) => ({
              id: item.id,
              descricao: item.descricao || item.nome || '',
              isCampoExperiencia: !!item.isCampoExperiencia,
              etapaMatrizDisciplina: item.etapaMatrizDisciplina as { id: number } | undefined
            }));

            if (this.instrumentoForm.itemEducacionalId && !this.instrumentoItensDisponiveis.some((item) => item.id === this.instrumentoForm.itemEducacionalId)) {
              this.instrumentoForm.itemEducacionalId = null;
            }
            resolve();
          },
          error: () => {
            this.instrumentoItensDisponiveis = [];
            resolve();
          }
        });
    });
  }

  private loadInstrumentoConfiguracoes(): Promise<void> {
    if (!this.instrumentoForm.turmaId) {
      this.instrumentoConfiguracaoEstabelecimento = null;
      this.instrumentoConfiguracaoTurma = null;
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      combineLatest([
        this.instrumentoAvaliacaoService.getConfiguracaoEstabelecimento(this.instrumentoForm.turmaId!),
        this.avaliacaoService.getConfiguracaoAvaliacao(this.instrumentoForm.turmaId!)
      ])
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: ([configEstabelecimento, configTurma]) => {
            this.instrumentoConfiguracaoEstabelecimento = configEstabelecimento || null;
            this.instrumentoConfiguracaoTurma = (configTurma || null) as Record<string, unknown> | null;
            resolve();
          },
          error: () => {
            this.instrumentoConfiguracaoEstabelecimento = null;
            this.instrumentoConfiguracaoTurma = null;
            resolve();
          }
        });
    });
  }

  private loadInstrumentoCalendario(): Promise<void> {
    const estabelecimentoId = this.instrumentoTurmaSelecionada?.estabelecimento?.id;
    if (!estabelecimentoId) {
      this.instrumentoCalendario = null;
      return Promise.resolve();
    }

    return this.calendarioService.getCalendario(estabelecimentoId).then((calendario) => {
      this.instrumentoCalendario = calendario;
    });
  }

  private loadInstrumentoEncerramentos(): Promise<void> {
    if (!this.instrumentoForm.turmaId) {
      this.instrumentoEncerramentosTurma = [];
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.avaliacaoService.getEncerramentosByTurma(this.instrumentoForm.turmaId!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (encerramentos) => {
            this.instrumentoEncerramentosTurma = Array.isArray(encerramentos) ? encerramentos as Array<Record<string, unknown>> : [];
            resolve();
          },
          error: () => {
            this.instrumentoEncerramentosTurma = [];
            resolve();
          }
        });
    });
  }

  private loadInstrumentoPeriodos(): Promise<void> {
    if (!this.instrumentoForm.itemEducacionalId) {
      this.instrumentoPeriodos = [];
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.instrumentoAvaliacaoService.getPeriodosItemAvaliacao(this.instrumentoForm.itemEducacionalId!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (periodos) => {
            this.instrumentoPeriodos = periodos || [];

            if (this.instrumentoTurmaEAtividade && this.instrumentoPeriodos.length) {
              this.instrumentoForm.modoAvaliacao = this.instrumentoPeriodos[0].modoAvaliacao || undefined;
            }

            if (!this.instrumentoTurmaEAtividade && this.instrumentoForm.periodoAvaliativoId) {
              const periodoDetalhado = this.findInstrumentoPeriodoDetalhado(this.instrumentoForm.periodoAvaliativoId);
              this.instrumentoForm.modoAvaliacao = periodoDetalhado?.modoAvaliacao || undefined;
            }

            if (this.instrumentoTurmaEAtividade) {
              this.instrumentoForm.periodoAvaliativoId = null;
            } else if (!this.findInstrumentoPeriodoDetalhado(this.instrumentoForm.periodoAvaliativoId)) {
              const primeiroPeriodo = this.instrumentoPeriodos[0];
              this.instrumentoForm.periodoAvaliativoId = Number(primeiroPeriodo?.periodoAvaliativo?.id ?? primeiroPeriodo?.id ?? 0) || null;
            }
            resolve();
          },
          error: () => {
            this.instrumentoPeriodos = [];
            resolve();
          }
        });
    });
  }

  private loadInstrumentosContexto(): Promise<void> {
    if (!this.instrumentoForm.itemEducacionalId) {
      this.instrumentoContextoRegistros = [];
      return Promise.resolve();
    }

    const mesmoContextoTelaAtual = this.instrumentoForm.turmaId === this.turma?.id &&
      this.instrumentoForm.itemEducacionalId === this.itemEducacional?.id &&
      (this.instrumentoTurmaEAtividade || this.instrumentoForm.periodoAvaliativoId === this.periodo?.id);

    if (mesmoContextoTelaAtual && this.instrumentosResumo.length) {
      this.instrumentoContextoRegistros = this.instrumentosResumo.map((item) => item.raw as InstrumentoAvaliacaoRegistro);
      return Promise.resolve();
    }

    let filter = `itemEducacional = ${this.instrumentoForm.itemEducacionalId}`;
    if (!this.instrumentoTurmaEAtividade && this.instrumentoForm.periodoAvaliativoId) {
      filter += ` and periodoAvaliativo = ${this.instrumentoForm.periodoAvaliativoId}`;
    }

    return new Promise((resolve) => {
      this.instrumentoAvaliacaoService.list({ filter, offset: 0, limit: 999, sort: 'data asc' })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.hydrateInstrumentosComPublicacao(response?.content || []).then((registros) => {
              this.instrumentoContextoRegistros = registros;
              resolve();
            });
          },
          error: () => {
            this.instrumentoContextoRegistros = [];
            resolve();
          }
        });
    });
  }

  private loadInstrumentosTelaAtual(): Promise<void> {
    if (!this.turma?.id) {
      this.instrumentosTelaRegistros = [];
      return Promise.resolve();
    }

    const itemEducacionalIds = Array.from(new Set(
      (this.contextoItemEducacionalIds || []).filter((id) => Number(id) > 0)
    ));

    if (!itemEducacionalIds.length) {
      this.instrumentosTelaRegistros = [];
      return Promise.resolve();
    }

    const requests: Observable<{ content: InstrumentoAvaliacaoRegistro[] }>[] = itemEducacionalIds.map((id) => {
      const filter = `itemEducacional = ${id}`;
      return this.instrumentoAvaliacaoService.list({ filter, offset: 0, limit: 999, sort: 'data asc' }).pipe(
        catchError(() => of({ content: [] }))
      );
    });

    return new Promise((resolve) => {
      forkJoin(requests)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (responses: Array<{ content: InstrumentoAvaliacaoRegistro[] }>) => {
            const registros = responses
              .flatMap((response) => response?.content || [])
              .filter((item, index, self) => self.findIndex((i) => i.id === item.id) === index);

            this.hydrateInstrumentosComPublicacao(registros).then((registrosHidratados) => {
              this.instrumentosTelaRegistros = registrosHidratados;
              resolve();
            });
          },
          error: () => {
            this.instrumentosTelaRegistros = [];
            resolve();
          }
        });
    });
  }

  private processarInstrumentoAnexos(files: File[]): void {
    const arquivosValidos = files.filter((file) => this.validarInstrumentoAnexo(file));
    if (!arquivosValidos.length) {
      return;
    }

    this.instrumentoAnexosEnviando = true;
    let uploadsPendentes = arquivosValidos.length;

    arquivosValidos.forEach((file) => {
      const arquivoTemporario: InstrumentoArquivoState = {
        id: `temp-${Date.now()}-${Math.random()}`,
        nome: file.name,
        tipo: file.type,
        tamanho: file.size,
        uploading: true,
        raw: {} as InstrumentoArquivoAnexo
      };

      this.instrumentoArquivos = [...this.instrumentoArquivos, arquivoTemporario];

      this.instrumentoAvaliacaoService.uploadAnexo(file)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            uploadsPendentes -= 1;
            this.instrumentoAnexosEnviando = uploadsPendentes > 0;
          })
        )
        .subscribe({
          next: (response) => {
            this.instrumentoArquivos = this.instrumentoArquivos.filter(
              (a) => a.id !== arquivoTemporario.id
            );
            const novoArquivo = this.mapInstrumentoArquivo(response, file.name, file.size, file.type);
            this.instrumentoArquivos = [...this.instrumentoArquivos, novoArquivo];
          },
          error: (error) => {
            this.instrumentoArquivos = this.instrumentoArquivos.filter(
              (a) => a.id !== arquivoTemporario.id
            );
            this.errorHandler.handleError(error);
          }
        });
    });
  }

  private baixarInstrumentoAnexo(anexo: InstrumentoArquivoState): void {
    if (!anexo?.id) {
      this.notificationService.warning({ title: 'Download indisponível', message: 'O arquivo ainda não está disponível para download.' });
      return;
    }

    const downloadUrl = this.instrumentoAvaliacaoService.getDownloadAnexoUrl(anexo.id);
    if (!downloadUrl) {
      this.notificationService.warning({ title: 'Download indisponível', message: 'Não foi possível obter as credenciais para baixar o anexo.' });
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.download = anexo.nome || 'anexo';
    anchor.click();
  }

  private buildInstrumentoPayload(): InstrumentoAvaliacaoRegistro {
    const periodoDetalhado = this.findInstrumentoPeriodoDetalhado(this.instrumentoForm.periodoAvaliativoId);
    const instrumentoRecuperacao = this.instrumentosRecuperacaoOptions.find((item) => item.id === this.instrumentoForm.instrumentoRecuperacaoId) || null;
    const instrumentoRecuperacaoDetalhado = instrumentoRecuperacao?.id
      ? this.instrumentoRecuperacaoDetalhes.get(instrumentoRecuperacao.id)
      : null;

    const payloadBase = construirInstrumentoPayload(this.instrumentoForm, {
      periodoDetalhado,
      instrumentoRecuperacao,
      instrumentoRecuperacaoDetalhado,
      modoAvaliacaoAtual: this.getModoAvaliacaoInstrumentoAtual(),
      turmaSelecionada: this.instrumentoTurmaSelecionada,
      itemSelecionado: this.instrumentoItemSelecionado,
      shouldShowInstrumentoPeriodoField: this.shouldShowInstrumentoPeriodoField,
      instrumentoPeriodoSelecionado: this.instrumentoPeriodoSelecionado,
      instrumentoRegistroOriginal: this.instrumentoRegistroOriginal,
      instrumentoTurmaEAtividade: this.instrumentoTurmaEAtividade,
      instrumentoPeriodos: this.instrumentoPeriodos,
      instrumentoExibeFaixaNotas: this.instrumentoExibeFaixaNotas,
      instrumentoExibePeso: this.instrumentoExibePeso,
      instrumentoArquivos: this.instrumentoArquivos,
      instrumentoCampoAdicional: this.instrumentoCampoAdicional
    });

    this.normalizarPayloadRecuperacaoParalela(payloadBase);

    return payloadBase;
  }

  private validarFormularioInstrumento(): string[] {
    const recuperacaoVinculada = this.instrumentoForm.id ? this.getRecuperacaoParalelaVinculada(this.instrumentoForm.id) : undefined;
    const data = this.parseDate(this.instrumentoForm.data);

    return validarInstrumentoFormulario(this.instrumentoForm, {
      labelItensEducacionais: this.labelItensEducacionais,
      shouldShowInstrumentoPeriodoField: this.shouldShowInstrumentoPeriodoField,
      instrumentoTurmaEjaModular: this.instrumentoTurmaEjaModular,
      instrumentoTurmaEAtividade: this.instrumentoTurmaEAtividade,
      instrumentoExibeFaixaNotas: this.instrumentoExibeFaixaNotas,
      instrumentoExibePeso: this.instrumentoExibePeso,
      instrumentoPesoMinimoLimite: this.instrumentoPesoMinimoLimite,
      instrumentoPesoMaximoLimite: this.instrumentoPesoMaximoLimite,
      instrumentoConfiguracaoTurma: this.instrumentoConfiguracaoTurma,
      instrumentoConfiguracaoEstabelecimento: this.instrumentoConfiguracaoEstabelecimento,
      instrumentoContextoResumo: this.instrumentoContextoResumo,
      instrumentosRecuperacaoOptions: this.instrumentosRecuperacaoOptions,
      rangePeriodoSelecionado: this.shouldShowInstrumentoPeriodoField ? this.getRangePeriodoSelecionado() : null,
      diaLetivoValido: data
        ? this.calendarioService.verificaDiaLetivo(data, this.instrumentoCalendario, this.getPeriodoSelecionadoParaValidacaoLetiva(), undefined)
        : true,
      alteracaoManualPeriodoAvaliativo: this.getAlteracoesFormularioInstrumento().some(({ campo }) => campo === 'periodoAvaliativoId'),
      anoLetivoAtual: this.getAnoLetivoAtual(),
      alteracoesBloqueadasPorRecuperacaoVinculada: recuperacaoVinculada
        ? this.getAlteracoesCriticasInstrumento().filter((campo) => campo !== 'data' && campo !== 'período avaliativo')
        : [],
      instrumentoCamposAdicionaisObrigatoriosValidos: this.instrumentoCamposAdicionaisObrigatoriosValidos
    });
  }

  private salvarInstrumentoComRecuperacaoVinculada(
    payload: InstrumentoAvaliacaoRegistro,
    recuperacaoVinculada: InstrumentoResumoView,
    adicionarNovo: boolean
  ): void {
    this.instrumentoAvaliacaoService.get(recuperacaoVinculada.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (recuperacaoRegistro) => {
          this.instrumentoSalvando = false;
          this.instrumentosFacade.abrirRecuperacaoVinculada(
            payload,
            this.buildRecuperacaoVinculadaEditable(recuperacaoRegistro, payload),
            adicionarNovo
          );
        },
        error: (error) => {
          this.instrumentoSalvando = false;
          this.errorHandler.handleError(error);
        }
      });
  }

  private finalizarSucessoInstrumento(adicionarNovo: boolean): void {
    this.instrumentoSalvando = false;
    this.instrumentoRegistroOriginal = null;
    this.notificationService.success({ title: 'Instrumento salvo', message: 'O instrumento de avaliação foi salvo com sucesso.' });
    this.syncContextFromInstrumentoForm();
    this.loadInstrumentosTelaAtual().catch(() => undefined);

    if (adicionarNovo) {
      this.instrumentoForm = createInstrumentoFormulario({
        turmaId: this.turma?.id || null,
        itemEducacionalId: this.itemEducacional?.id || null,
        periodoAvaliativoId: this.periodo?.id || null
      });
      this.instrumentoArquivos = [];
      this.resetInstrumentoCampoAdicional();
      this.syncInstrumentoFormWithContext();
      this.carregarDadosFormularioInstrumento()
        .then(() => this.carregarCampoAdicionalInstrumento(true))
        .finally(() => {
          this.instrumentoSnapshotInicial = this.serializeInstrumentoForm();
          this.instrumentoArquivosSnapshotInicial = this.serializeInstrumentoArquivos();
          this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
          this.instrumentoMostrarErros = false;
        });
      return;
    }

    this.fecharFormularioInstrumento();
  }

  private hasInstrumentoCampoAdicionalConteudo(value: InstrumentoCampoAdicionalConfig): boolean {
    return hasInstrumentoCampoAdicionalConteudo(value);
  }

  private carregarCampoAdicionalInstrumento(force = false): Promise<void> {
    const instrumentoId = this.instrumentoForm.id || this.instrumentoRegistroOriginal?.id || null;
    if (!instrumentoId) {
      if (!force && this.instrumentoCamposAdicionaisTemplateCarregado) {
        return Promise.resolve();
      }

      this.instrumentoCamposAdicionaisCarregando = true;
      return new Promise((resolve) => {
        this.instrumentoAvaliacaoService.getCamposAdicionaisTemplate()
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (campoAdicional) => {
              this.instrumentoCampoAdicional = cloneInstrumentoCampoAdicional(campoAdicional as InstrumentoCampoAdicionalConfig);
              this.instrumentoCamposAdicionaisTemplateCarregado = true;
              this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
              this.instrumentoCamposAdicionaisCarregando = false;
              resolve();
            },
            error: (error) => {
              this.instrumentoCampoAdicional = null;
              this.instrumentoCamposAdicionaisTemplateCarregado = false;
              this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
              this.instrumentoCamposAdicionaisCarregando = false;
              resolve();
            }
          });
      });
    }

    if (!force && this.instrumentoCamposAdicionaisCarregadoParaId === instrumentoId) {
      return Promise.resolve();
    }

    this.instrumentoCamposAdicionaisCarregando = true;
    return new Promise((resolve) => {
      this.instrumentoAvaliacaoService.getCampoAdicional(instrumentoId)
        .pipe(takeUntil(this.closeInstrumentoFormulario$))
        .subscribe({
          next: (campoAdicional) => {
            const possuiCampoAdicionalAtual = this.hasInstrumentoCampoAdicionalConteudo(this.instrumentoCampoAdicional);
            const campoAdicionalResposta = cloneInstrumentoCampoAdicional(campoAdicional as InstrumentoCampoAdicionalConfig);
            const possuiCampoAdicionalResposta = this.hasInstrumentoCampoAdicionalConteudo(campoAdicionalResposta);

            if (possuiCampoAdicionalResposta || !possuiCampoAdicionalAtual) {
              this.instrumentoCampoAdicional = campoAdicionalResposta;
            }
            this.instrumentoCamposAdicionaisCarregadoParaId = instrumentoId;
            this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
            this.instrumentoCamposAdicionaisCarregando = false;
            resolve();
          },
          error: () => {
            if (!this.hasInstrumentoCampoAdicionalConteudo(this.instrumentoCampoAdicional)) {
              this.instrumentoCampoAdicional = null;
            }
            this.instrumentoCamposAdicionaisCarregadoParaId = null;
            this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
            this.instrumentoCamposAdicionaisCarregando = false;
            resolve();
          }
        });
    });
  }

  private syncContextFromInstrumentoForm(): void {
    const itemEducacionalId = this.instrumentoForm.itemEducacionalId;
    if (itemEducacionalId) {
      const itemSelecionado = this.itensEducacionais.find((item) => item.id === itemEducacionalId)
        || this.instrumentoItensDisponiveis.find((item) => item.id === itemEducacionalId)
        || null;
      if (itemSelecionado) {
        this.itemEducacional = itemSelecionado;
      }
    }

    if (!this.instrumentoTurmaEAtividade && this.instrumentoForm.periodoAvaliativoId) {
      let periodoSelecionado = this.periodos.find((periodo) => periodo.id === this.instrumentoForm.periodoAvaliativoId) || null;
      const periodoDetalhado = this.findInstrumentoPeriodoDetalhado(this.instrumentoForm.periodoAvaliativoId);
      if (!periodoSelecionado && periodoDetalhado) {
        periodoSelecionado = {
          id: Number(periodoDetalhado.periodoAvaliativo?.id || periodoDetalhado.id || 0),
          descricao: String(periodoDetalhado.periodoAvaliativo?.descricao || periodoDetalhado.descricao || ''),
          dataInicial: String(periodoDetalhado.periodoAvaliativo?.dataInicial || periodoDetalhado.dataInicial || ''),
          dataFinal: String(periodoDetalhado.periodoAvaliativo?.dataFinal || periodoDetalhado.dataFinal || '')
        };
      }
      if (periodoSelecionado) {
        this.periodo = periodoSelecionado;
        this.periodos = [periodoSelecionado];
      }
    }
  }

  private getRecuperacaoParalelaVinculada(instrumentoId: number | null): InstrumentoResumoView | undefined {
    return encontrarRecuperacaoParalelaVinculada(this.instrumentoContextoResumo, this.instrumentosTelaResumo, instrumentoId);
  }

  private isPeriodoEncerradoComLista(periodo: PeriodoContexto | null, encerramentos: Array<Record<string, unknown>>): boolean {
    return isPeriodoEncerradoComLista(periodo, encerramentos);
  }

  private isPeriodoLetivoEncerrado(encerramentos: Array<Record<string, unknown>>): boolean {
    return isPeriodoLetivoEncerrado(encerramentos);
  }

  private hydrateInstrumentosComPublicacao(registros: InstrumentoAvaliacaoRegistro[]): Promise<InstrumentoAvaliacaoRegistro[]> {
    const pendentes = (registros || []).filter((registro) => Number(registro.id || 0) > 0 && !this.hasInstrumentoPublicacaoState(registro));
    if (!pendentes.length) {
      return Promise.resolve(registros || []);
    }

    return new Promise((resolve) => {
      forkJoin(
        pendentes.map((registro) => this.instrumentoAvaliacaoService.get(Number(registro.id || 0)).pipe(catchError(() => of(registro))))
      ).subscribe({
        next: (detalhes) => {
          const detalhesPorId = new Map<number, InstrumentoAvaliacaoRegistro>();
          detalhes.forEach((registroDetalhado) => {
            const id = Number(registroDetalhado.id || 0);
            if (id > 0) {
              detalhesPorId.set(id, { ...registroDetalhado });
            }
          });

          resolve((registros || []).map((registro) => detalhesPorId.get(Number(registro.id || 0)) || registro));
        },
        error: () => resolve(registros || [])
      });
    });
  }

  private atualizarEstadoPublicacaoInstrumento(instrumentoId: number, publicado: boolean, dataPublicacao: string | null): void {
    const atualizarRegistro = (registro: InstrumentoAvaliacaoRegistro): InstrumentoAvaliacaoRegistro =>
      aplicarPublicacaoInstrumento(registro, instrumentoId, publicado, dataPublicacao);

    this.instrumentosTelaRegistros = this.instrumentosTelaRegistros.map(atualizarRegistro);
    this.instrumentoContextoRegistros = this.instrumentoContextoRegistros.map(atualizarRegistro);
  }

  private getModoAvaliacaoInstrumentoAtual(): string | undefined {
    if (this.instrumentoForm.instrumentoRecuperacaoId) {
      return this.instrumentosRecuperacaoOptions.find((item) => item.id === this.instrumentoForm.instrumentoRecuperacaoId)?.modoAvaliacao;
    }

    if (this.instrumentoTurmaEAtividade) {
      return this.instrumentoPeriodos[0]?.modoAvaliacao || this.instrumentoForm.modoAvaliacao;
    }

    return this.findInstrumentoPeriodoDetalhado(this.instrumentoForm.periodoAvaliativoId)?.modoAvaliacao || this.instrumentoForm.modoAvaliacao;
  }

  protected sanitizeInstrumentoCampoNumerico(value: unknown, campo?: InstrumentoCampoNumerico): string {
    return sanitizeInstrumentoCampoNumerico(value, campo, this.instrumentoPesoMaximoLimite, this.instrumentoConfiguracaoTurma);
  }

  protected extractTurmaIdFromRaw(raw: Record<string, any>): number | null {
    return extractTurmaIdFromRaw(raw);
  }

  protected getTipoInstrumentoLabel(tipo: TipoInstrumentoAvaliacaoId | string | null | undefined): string {
    return getTipoInstrumentoLabel(tipo);
  }

  formatarDataHoraPublicacao(dataPublicacao: string): string {
    return formatarDataHoraPublicacao(dataPublicacao);
  }

  private isInstrumentoBloqueadoPorEncerramento(instrumento: InstrumentoResumoView): boolean {
    if (!instrumento.periodoAvaliativoId || !this.turma?.id) {
      return false;
    }

    const periodo = this.periodos.find((item) => item.id === instrumento.periodoAvaliativoId) || null;
    return periodo ? this.isPeriodoEncerradoComLista(periodo, this.instrumentoEncerramentosTurma) : false;
  }

}
